import { Component, Input, OnInit } from "@angular/core";
import { NgModel } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { NprConfiguration } from "src/app/app.configuration";
import { Team } from "src/app/generated-models";
import { PlanOnboard } from "src/app/generated-models/PlanOnboard";
import { BlueprintType, ITeamListRecord, IUserProfile } from "src/app/npr-request.model";
import { ApiService } from "src/app/services";
import { PlanKindDisplayEnum, PlanKindEnum } from "src/app/shared/enums/plan-enums";
import { LoadingScreenService } from "src/app/shared/loading-screen.service";
import { isNonEmptyString } from "src/app/utility/common-helper";

export interface PlanOnboardingModalOptions {
  save: (serviceTreeId: string, payload: PlanOnboard) => Promise<any>;
  type: BlueprintType;
  teams?: string[];
}

@Component({
  standalone: false,
  templateUrl: "./plan-onboarding-modal.component.html",
  styleUrls: ["../../../../styles.scss"],
})
export class PlanOnboardingModalComponent implements OnInit {
  @Input() options: PlanOnboardingModalOptions;
  planNameRegExp = new RegExp("^[A-Za-z][A-Za-z0-9]*$");
  maxPlanNameLength = 100;
  guidRegExpression = NprConfiguration.guidRegExpression;
  team: ITeamListRecord;
  teamList: ITeamListRecord[];
  userProfile: IUserProfile;
  isOnboardEnabled: boolean;
  teamOid: string;
  customTeam = false;
  planName: string;
  planKind: PlanKindEnum = PlanKindEnum.Other;
  needsQuotaInEveryRegion = false;
  isAdmin = false;
  readonly PlanKindEnum = PlanKindEnum;

  planKindList: { key: string; value: string; disabled: boolean }[] = this.buildPlanKindList();

  private buildPlanKindList(): { key: string; value: string; disabled: boolean }[] {
    return Object.keys(PlanKindDisplayEnum).map((k) => ({
      key: k,
      value: (PlanKindDisplayEnum as any)[k],
      disabled: k === PlanKindEnum.NewRegion && !this.needsQuotaInEveryRegion,
    }));
  }

  constructor(
    public activeModal: NgbActiveModal,
    private apiService: ApiService,
    private loadingService: LoadingScreenService,
    private notificationService: ToastrService
  ) {
    this.teamList = [];
  }

  async ngOnInit() {
    this.apiService
      .getUserProfile()
      .then((response) => {
        if (response) {
          this.userProfile = response;
          this.isAdmin = this.userProfile.IsAdmin;
          this.isOnboardEnabled = this.userProfile.IsAdmin || this.userProfile.UserRoles.some((_) => _.Services.length > 0);
          this.getTeamsList();
        } else {
          this.notificationService.error("user profile returns null, no data was loaded.");
        }
      })
      .catch((error) => {
        this.notificationService.error(`unable to get user profile, error: ${error.message}`);
      });
  }

  getTeamsList(): void {
    this.loadingService.setLoading(true);
    // Team List
    this.apiService?.getTeamList().subscribe(
      (response) => {
        let teams: Team[] = [];
        if (response) {
          if (this.userProfile.IsAdmin || (this.isPfBlueprintType() && this.userProfile.IsPfAdmin)) {
            teams = response;
          } else {
            response.map((r) => {
              if (this.userProfile.UserRoles.some((_) => _.Services.includes(r.Oid))) {
                teams.push(r);
              }
            });
          }
        }

        const regionlessTeam = [];
        teams.map((r) => {
          if (r.Ring) {
            this.teamList.push({
              label: `${r.Name} (${r.Oid})`,
              value: 0,
              ring: r.Ring,
              oid: r.Oid,
            } as ITeamListRecord);
          } else {
            regionlessTeam.push({
              label: `${r.Name} (${r.Oid})`,
              value: 0,
              ring: "No Ring",
              oid: r.Oid,
            } as ITeamListRecord);
          }
        });
        this.teamList = this.teamList
          .sort((a, b) => {
            if (a.label === null) {
              console.log(a);
            }
            return a.ring.localeCompare(b.ring) || a.label.localeCompare(b.label);
          })
          .concat(regionlessTeam.sort((a, b) => a.label.localeCompare(b.label)));
        this.loadingService.setLoading(false);
      },
      (e: unknown) => {
        this.loadingService.setLoading(false);
        this.notificationService.error(e as string);
      }
    );
  }

  disableSubmit(): boolean {
    if (this.customTeam) {
      return !this.teamOid || !RegExp(this.guidRegExpression).test(this.teamOid);
    } else {
      return !this.team;
    }
  }

  async submit() {
    if (this.disableSubmit()) {
      this.notificationService.warning(`Please select a service or enter a valid service tree ID.`);
      return;
    } else {
      try {
        const payload: PlanOnboard = this.isNewOnboarding()
          ? { PlanName: "GA", PlanKind: PlanKindEnum.NewRegion }
          : { PlanName: this.planName, PlanKind: this.planKind ?? PlanKindEnum.Other };
        const result = await this.options.save(this.team?.oid || this.teamOid, payload);
        this.activeModal.close(result);
      } catch (e) {
        this.notificationService.error(e);
      }
    }
  }

  isTouchedOrDirty(control: NgModel) {
    return control && (control.touched || control.dirty);
  }

  isPlannedQuotaV2Supported(): boolean {
    return this.options.type === BlueprintType.PlannedQuota;
  }

  isPfBlueprintType(): boolean {
    return this.options.type === BlueprintType.PF;
  }

  // Check if the team is new for onboarding
  isNewOnboarding(): boolean {
    return !this.options.teams?.includes(this.team?.oid || this.teamOid);
  }

  // Reset PlanKind to 'Other' if 'NewRegion' is selected when unchecking the every-region checkbox.
  onEveryRegionChange(): void {
    this.planKindList = this.buildPlanKindList();
    if (!this.needsQuotaInEveryRegion && this.planKind === PlanKindEnum.NewRegion) {
      this.planKind = PlanKindEnum.Other;
    }
  }

  get isNameValid() {
    return isNonEmptyString(this.planName) && this.planName.length <= this.maxPlanNameLength && this.planNameRegExp.test(this.planName);
  }
}
